/**
 * La rutina es DATOS (pestaña `Rutina` del libro): cada ejercicio tiene nombre, cómo se mide, cantidad y qué se
 * pregunta al terminarlo en la última ronda guiada. Así se pueden añadir, quitar (archivar) o renombrar ejercicios
 * sin tocar código. Los ids de los 6 ejercicios de siempre se conservan para que el historial siga cuadrando.
 */

export type Section = 'FLOOR' | 'NORMAL';
/** Repeticiones, repeticiones por lado, o segundos (isométricos como la plancha). */
export type Measure = 'reps' | 'reps_side' | 'seconds';
/** Qué se pregunta tras el ejercicio en la ronda guiada: RIR, control (sí/regular/no) o solo «hecho». */
export type Question = 'rir' | 'control' | 'done';

export interface Exercise {
  id: string;
  section: Section;
  order: number;
  name: string;
  measure: Measure;
  target: number;
  question: Question;
  /** Pista bajo la pregunta de RIR, o el texto de la pregunta de control. */
  prompt: string;
  active: boolean;
}

export interface WorkoutConfig {
  maxRounds: number;
  ceilingSeconds: number;
  targetNormals: number;
  floor: Exercise[];
  normal: Exercise[];
}

export const MEASURE_LABEL: Record<Measure, string> = { reps: 'repeticiones', reps_side: 'por lado', seconds: 'segundos' };

/** Cantidad a la derecha de cada fila: «10», «5 / lado», «10 s». */
export function quantity(e: Pick<Exercise, 'measure' | 'target'>): string {
  return e.measure === 'reps_side' ? `${e.target} / lado` : e.measure === 'seconds' ? `${e.target} s` : `${e.target}`;
}

/** Para el texto de revisión: «12», «6/lado», «10 s». */
export function plainQuantity(e: Pick<Exercise, 'measure' | 'target'>): string {
  return e.measure === 'reps_side' ? `${e.target}/lado` : e.measure === 'seconds' ? `${e.target} s` : `${e.target}`;
}

/** Id estable para un ejercicio nuevo a partir de su nombre (sin chocar con los existentes). */
export function exerciseId(name: string, taken: ReadonlySet<string>): string {
  const base = name.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 30) || 'ejercicio';
  let id = base;
  for (let i = 2; taken.has(id); i++) id = `${base}_${i}`;
  return id;
}

/** Valores de partida (Code.gs DEFAULT_SETTINGS) por si el libro no tiene ni `Rutina` ni las claves de `Settings`. */
export const DEFAULT_TARGETS = {
  FLOOR_PUSHUPS: 5, FLOOR_SQUATS: 5, FLOOR_SPLIT_SQUAT_PER_LEG: 1, FLOOR_PLANK_SECONDS: 10,
  NORMAL_PUSHUPS: 8, NORMAL_SQUATS: 8, NORMAL_SPLIT_SQUAT_PER_LEG: 5, NORMAL_GLUTE_BRIDGES: 10, NORMAL_DEAD_BUG_PER_SIDE: 4,
} as const;

const SPLIT_HINT = 'Responde por la pierna más limitada.';
const LUMBAR = '¿Has mantenido el control lumbar?';

/** Rutina inicial a partir de las claves FLOOR_* / NORMAL_* de `Settings` (la que usaban la web y iOS). */
export function routineFromSettings(settings: Record<string, string>): Exercise[] {
  const n = (key: keyof typeof DEFAULT_TARGETS) => {
    const v = Number(settings[key]);
    return Number.isFinite(v) && v > 0 ? Math.round(v) : DEFAULT_TARGETS[key];
  };
  const ex = (section: Section, order: number, id: string, name: string, measure: Measure, target: number, question: Question, prompt = ''): Exercise =>
    ({ id, section, order, name, measure, target, question, prompt, active: true });
  return [
    ex('FLOOR', 1, 'pushup', 'Flexiones', 'reps', n('FLOOR_PUSHUPS'), 'rir'),
    ex('FLOOR', 2, 'squat', 'Sentadillas profundas', 'reps', n('FLOOR_SQUATS'), 'rir'),
    ex('FLOOR', 3, 'split_squat', 'Split squat', 'reps_side', n('FLOOR_SPLIT_SQUAT_PER_LEG'), 'rir', SPLIT_HINT),
    ex('FLOOR', 4, 'plank', 'Plancha sobre antebrazos', 'seconds', n('FLOOR_PLANK_SECONDS'), 'done'),
    ex('NORMAL', 1, 'pushup', 'Flexiones', 'reps', n('NORMAL_PUSHUPS'), 'rir'),
    ex('NORMAL', 2, 'squat', 'Sentadillas profundas', 'reps', n('NORMAL_SQUATS'), 'rir'),
    ex('NORMAL', 3, 'split_squat', 'Split squat', 'reps_side', n('NORMAL_SPLIT_SQUAT_PER_LEG'), 'rir', SPLIT_HINT),
    ex('NORMAL', 4, 'glute_bridge', 'Puentes de glúteo', 'reps', n('NORMAL_GLUTE_BRIDGES'), 'rir'),
    ex('NORMAL', 5, 'dead_bug', 'Dead bug', 'reps_side', n('NORMAL_DEAD_BUG_PER_SIDE'), 'control', LUMBAR),
  ];
}

/** Ejercicios activos de una sección, en orden. */
export const activeOf = (routine: readonly Exercise[], section: Section) =>
  routine.filter(e => e.section === section && e.active).sort((a, b) => a.order - b.order);

/** Configuración del entreno a partir de `Settings` y la rutina. */
export function workoutConfig(settings: Record<string, string>, routine: readonly Exercise[]): WorkoutConfig {
  const n = (key: string, fallback: number, min = 1) => {
    const v = Number(settings[key]);
    return Number.isFinite(v) && v >= min ? Math.round(v) : fallback;
  };
  return {
    maxRounds: n('MAX_ROUNDS', 2),
    ceilingSeconds: n('CEILING_MINUTES', 8) * 60,
    targetNormals: n('CALIBRATION_TARGET_NORMALS', 4),
    floor: activeOf(routine, 'FLOOR'),
    normal: activeOf(routine, 'NORMAL'),
  };
}

/** Foto de la rutina del día, guardada en la sesión (columna `routineSnapshot`) para el historial y las revisiones. */
export function routineSnapshot(config: WorkoutConfig): string {
  const pick = (e: Exercise) => ({ id: e.id, name: e.name, measure: e.measure, target: e.target, question: e.question });
  return JSON.stringify({ maxRounds: config.maxRounds, ceilingMinutes: config.ceilingSeconds / 60, floor: config.floor.map(pick), normal: config.normal.map(pick) });
}

export interface SnapshotExercise { id: string; name: string; measure: Measure; target: number; question: Question }
export interface RoutineSnapshot { maxRounds: number; ceilingMinutes: number; floor: SnapshotExercise[]; normal: SnapshotExercise[] }

export function parseRoutineSnapshot(raw: string): RoutineSnapshot | null {
  try {
    const v = JSON.parse(raw) as RoutineSnapshot;
    return v && Array.isArray(v.normal) && Array.isArray(v.floor) ? v : null;
  } catch { return null; }
}

// ---------------------------------------------------------------------------------------------------------------
// Respuestas de la ronda guiada (ExerciseMeasurement.swift). Mismos códigos en la columna `exerciseFeedback`.

/** «0»…«4», «5+», «unknown» (no sé: hecho sin valorar), «done», «lumbar:yes|partial|no», «skipped» (no hecho). */
export type MeasurementCode = string;

export const RIR_CHOICES: MeasurementCode[] = ['0', '1', '2', '3', '4', '5+'];
export const CONTROL_CHOICES: MeasurementCode[] = ['lumbar:yes', 'lumbar:partial', 'lumbar:no'];

export const wasPerformed = (code: MeasurementCode | undefined) => code !== undefined && code !== 'skipped';

export function isValidCode(code: string): boolean {
  return /^[0-4]$/.test(code) || ['5+', 'unknown', 'done', 'skipped', ...CONTROL_CHOICES].includes(code);
}

/** Etiqueta del botón. */
export function choiceLabel(code: MeasurementCode): string {
  switch (code) {
    case 'unknown': return 'No sé (sí lo he hecho)';
    case 'done': return 'Hecho';
    case 'skipped': return 'Saltar';
    case 'lumbar:yes': return 'Sí';
    case 'lumbar:partial': return 'Regular';
    case 'lumbar:no': return 'No';
    default: return code;
  }
}

/** Resumen corto para el historial y las revisiones. */
export function summaryText(code: MeasurementCode): string {
  if (/^[0-4]$/.test(code) || code === '5+') return `RIR ${code}`;
  switch (code) {
    case 'unknown': return 'No sé';
    case 'done': return 'Hecho';
    case 'skipped': return 'Saltado';
    case 'lumbar:yes': return 'Control: sí';
    case 'lumbar:partial': return 'Control: regular';
    case 'lumbar:no': return 'Control: no';
    default: return code;
  }
}

/** Pregunta que se hace tras un ejercicio. Los isométricos (segundos) solo piden «hecho». */
export function questionOf(e: Pick<Exercise, 'question' | 'measure'>): Question {
  return e.measure === 'seconds' ? 'done' : e.question;
}

export function questionText(e: Pick<Exercise, 'question' | 'measure' | 'prompt'>): string {
  switch (questionOf(e)) {
    case 'rir': return '¿Cuántas repeticiones más podrías haber hecho con buena técnica?';
    case 'control': return e.prompt || '¿Has mantenido la técnica?';
    case 'done': return '¿Hecho?';
  }
}

/** `{"pushup":"2","dead_bug":"lumbar:yes"}` en el orden de la ronda (mismo formato que iOS). */
export function encodeFeedback(answers: Record<string, MeasurementCode>, order: readonly string[]): string {
  const ids = [...order.filter(id => id in answers), ...Object.keys(answers).filter(id => !order.includes(id)).sort()];
  return ids.length ? JSON.stringify(Object.fromEntries(ids.map(id => [id, answers[id]!]))) : '';
}

export function decodeFeedback(raw: string): Record<string, MeasurementCode> {
  try {
    const v = JSON.parse(raw) as Record<string, unknown>;
    return Object.fromEntries(Object.entries(v ?? {}).filter((e): e is [string, string] => typeof e[1] === 'string' && isValidCode(e[1])));
  } catch { return {}; }
}
